import { Capacitor } from '@capacitor/core';
import { Purchases, PRODUCT_CATEGORY, PURCHASES_ERROR_CODE, type CustomerInfo, type PurchasesStoreProduct } from '@revenuecat/purchases-capacitor';
import {
  BILLING_ENTITLEMENT_ID,
  BILLING_PRODUCT_IDS,
  isOneTimeProduct,
  type BillingProductKey,
  type PlanKind
} from '../../supabase/functions/_shared/billingPolicy';
import type { PurchaseFailureCode, PurchasesAdapter, StoreEntitlement, StoreIdentityOutcome, StorePurchaseOutcome } from './purchasesContract';

// The real purchase lane: App Store sheet through RevenueCat (@revenuecat/purchases-capacitor
// 13.5.1 — peerDependency @capacitor/core >= 8.0.0, verified in its changelog: Capacitor 8 support
// landed in 12.0.0 and 13.x keeps it). Design points, each one a hard-won platform fact:
//
//   - the plugin is imported STATICALLY, like the camera and the sign-in plugins: a dynamic
//     import() chunk that stalls on device is a documented old-line failure mode;
//   - the SDK is NEVER configured anonymously and logOut() is NEVER called. An anonymous
//     RevenueCat user can hold and restore an entitlement, which is exactly how a purchase lands on
//     someone else's account (memory yanwushi-auth-gate §2). The app user id is always the
//     signed-in Supabase user id, and the id is re-read and verified before anything is bought;
//   - one-time products are queried in the NON_SUBSCRIPTION catalogue and subscriptions in the
//     SUBSCRIPTION one — asking for a one-time product as a subscription returns an empty list on
//     Google Play (memory yanwushi-android-platform);
//   - a cancelled sheet is a neutral outcome, never an error; Ask to Buy answers 'pending';
//   - nothing here grants anything. The server decides what the account has (X-7); this lane only
//     performs the purchase and reports what the store said.
//
// Windows has no Xcode: this file is type-checked and bundled here, and its behaviour on a device
// is DEVICE TEST REQUIRED (reported as such, never claimed).
const IOS_KEY = ((import.meta.env.VITE_REVENUECAT_IOS_KEY as string | undefined) ?? '').trim();
// Android has its own RevenueCat public SDK key (goog_…), client-publishable like the iOS one
// (emulator walkthrough 2026-10-16: Android was configured with the iOS key).
const ANDROID_KEY = ((import.meta.env.VITE_REVENUECAT_ANDROID_KEY as string | undefined) ?? '').trim();

export type RevenueCatPlatform = 'ios' | 'android' | 'web' | string;

// The public key of the platform's store. Android NEVER falls back to the iOS key, and a key of the
// wrong store (appl_ in the Android slot, goog_ in the iOS slot) counts as no key: purchases then
// read "not set up" instead of failing inside the store.
export function revenueCatKeyFor(platform: RevenueCatPlatform, keys: { ios: string; android: string } = { ios: IOS_KEY, android: ANDROID_KEY }): string {
  if (platform === 'ios') {
    const key = keys.ios.trim();
    return key.startsWith('appl_') ? key : '';
  }
  if (platform === 'android') {
    const key = keys.android.trim();
    return key.startsWith('goog_') ? key : '';
  }
  return '';
}

const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';
const PLAY_SUBSCRIPTIONS_URL = 'https://play.google.com/store/account/subscriptions?package=com.nezha2capital.yanwushi';
export const PLAY_REDEEM_URL = 'https://play.google.com/redeem';

// Subscription management happens in the store's own page: RevenueCat's link when it has one (it does
// once a subscription exists), else the subscriptions page of the platform's store.
export function manageSubscriptionsUrlFor(platform: RevenueCatPlatform, url: string | null): string {
  if (url && url.length > 0) return url;
  return platform === 'android' ? PLAY_SUBSCRIPTIONS_URL : APPLE_SUBSCRIPTIONS_URL;
}

// Apple's redemption sheet exists on iOS only; Google Play redeems codes on its own page.
export function offerCodeRouteFor(platform: RevenueCatPlatform): 'appleSheet' | 'playRedeem' {
  return platform === 'android' ? 'playRedeem' : 'appleSheet';
}

function storeKeyHere(): string {
  return revenueCatKeyFor(Capacitor.getPlatform());
}

let configured = false;
let boundUserId: string | null = null;

function planOf(productIdentifier: string): PlanKind | null {
  // Google Play answers `<productId>:<basePlanId>`; iOS returns the bare id.
  const bare = productIdentifier.split(':')[0];
  if (bare === BILLING_PRODUCT_IDS.monthly) return 'monthly';
  if (bare === BILLING_PRODUCT_IDS.annual) return 'annual';
  return null;
}

function countTransactions(info: CustomerInfo, productId: string): number {
  return info.nonSubscriptionTransactions.filter((transaction) => transaction.productIdentifier.split(':')[0] === productId).length;
}

export function entitlementFrom(info: CustomerInfo, appUserId: string | null): StoreEntitlement {
  const entitlement = info.entitlements.active[BILLING_ENTITLEMENT_ID];
  const expiration = entitlement?.expirationDateMillis ?? null;
  const plan = entitlement ? planOf(entitlement.productIdentifier) : null;
  return {
    plan: entitlement && entitlement.isActive ? plan : null,
    activeUntil: entitlement && entitlement.isActive ? expiration : null,
    willRenew: entitlement ? entitlement.willRenew : null,
    managementUrl: info.managementURL,
    singlePurchases: countTransactions(info, BILLING_PRODUCT_IDS.single),
    addOnPurchases: countTransactions(info, BILLING_PRODUCT_IDS.addOn),
    appUserId
  };
}

function failureOf(raw: unknown): { code: PurchaseFailureCode; detail: string; cancelled: boolean; pending: boolean } {
  const error = raw as { code?: unknown; message?: unknown; userCancelled?: unknown } | null;
  const code = typeof error?.code === 'string' ? error.code : '';
  const message = typeof error?.message === 'string' ? error.message : String(raw ?? '');
  if (code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR || error?.userCancelled === true || /cancel/i.test(message)) {
    return { code: 'unknown', detail: message, cancelled: true, pending: false };
  }
  if (code === PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR) return { code: 'unknown', detail: message, cancelled: false, pending: true };
  if (code === PURCHASES_ERROR_CODE.NETWORK_ERROR) return { code: 'network', detail: message, cancelled: false, pending: false };
  if (code === PURCHASES_ERROR_CODE.PRODUCT_NOT_AVAILABLE_FOR_PURCHASE_ERROR || code === PURCHASES_ERROR_CODE.CONFIGURATION_ERROR) {
    return { code: 'productUnavailable', detail: message, cancelled: false, pending: false };
  }
  if (code === PURCHASES_ERROR_CODE.PURCHASE_NOT_ALLOWED_ERROR) return { code: 'notAllowed', detail: message, cancelled: false, pending: false };
  return { code: 'unknown', detail: message, cancelled: false, pending: false };
}

async function fetchProduct(product: BillingProductKey): Promise<PurchasesStoreProduct | null> {
  const identifier = BILLING_PRODUCT_IDS[product];
  try {
    const { products } = await Purchases.getProducts({
      productIdentifiers: [identifier],
      type: isOneTimeProduct(product) ? PRODUCT_CATEGORY.NON_SUBSCRIPTION : PRODUCT_CATEGORY.SUBSCRIPTION
    });
    // Play echoes `<productId>:<basePlanId>`, so an exact comparison finds nothing there.
    return products.find((candidate) => candidate.identifier === identifier || candidate.identifier.split(':')[0] === identifier) ?? null;
  } catch {
    return null;
  }
}

export const revenueCatAdapter: PurchasesAdapter = {
  platform: 'ios',

  configured(): boolean {
    return Capacitor.isNativePlatform() && storeKeyHere().length > 0;
  },

  async identify(appUserId: string): Promise<StoreIdentityOutcome> {
    const target = appUserId.trim();
    if (target.length === 0) return { ok: false, code: 'identity', detail: 'no signed-in user' };
    if (!Capacitor.isNativePlatform()) return { ok: false, code: 'notNative', detail: 'purchases run in the iOS / Android app' };
    const apiKey = storeKeyHere();
    if (apiKey.length === 0) return { ok: false, code: 'notConfigured', detail: `no ${Capacitor.getPlatform()} store key in this build` };
    try {
      if (!configured) {
        await Purchases.configure({ apiKey, appUserID: target });
        configured = true;
        boundUserId = target;
      } else if (boundUserId !== target) {
        await Purchases.logIn({ appUserID: target });
        boundUserId = target;
      }
      // Re-READ the identity instead of trusting what was just sent: if the SDK is bound to someone
      // else, nothing may be bought or restored under it.
      const { appUserID } = await Purchases.getAppUserID();
      if (appUserID !== target) {
        boundUserId = appUserID;
        return { ok: false, code: 'identity', detail: 'the store account does not match the signed-in user' };
      }
      const { customerInfo } = await Purchases.getCustomerInfo();
      return { ok: true, entitlement: entitlementFrom(customerInfo, appUserID) };
    } catch (raw) {
      const failure = failureOf(raw);
      return { ok: false, code: failure.code === 'unknown' ? 'identity' : failure.code, detail: failure.detail };
    }
  },

  async entitlement(): Promise<StoreEntitlement | null> {
    if (!configured) return null;
    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      return entitlementFrom(customerInfo, boundUserId);
    } catch {
      return null;
    }
  },

  async purchase(product: BillingProductKey): Promise<StorePurchaseOutcome> {
    if (!configured) return { kind: 'failed', code: 'notConfigured', detail: 'the store is not configured' };
    const storeProduct = await fetchProduct(product);
    if (!storeProduct) return { kind: 'failed', code: 'productUnavailable', detail: 'the store did not return this product' };
    try {
      // Hand the store's own object straight back: the plugin reads `identifier` and
      // `productCategory` off it to re-resolve the purchase, so a reconstructed id would not do.
      const { customerInfo } = await Purchases.purchaseStoreProduct({ product: storeProduct });
      return { kind: 'purchased', entitlement: entitlementFrom(customerInfo, boundUserId) };
    } catch (raw) {
      const failure = failureOf(raw);
      if (failure.cancelled) return { kind: 'cancelled' };
      if (failure.pending) return { kind: 'pending' };
      return { kind: 'failed', code: failure.code, detail: failure.detail };
    }
  },

  async restore(): Promise<StoreIdentityOutcome> {
    if (!configured) return { ok: false, code: 'notConfigured', detail: 'the store is not configured' };
    try {
      const { customerInfo } = await Purchases.restorePurchases();
      return { ok: true, entitlement: entitlementFrom(customerInfo, boundUserId) };
    } catch (raw) {
      const failure = failureOf(raw);
      return { ok: false, code: failure.code, detail: failure.detail };
    }
  },

  async presentOfferCodeSheet(): Promise<{ ok: true } | { ok: false; code: PurchaseFailureCode; detail: string }> {
    // Google Play redeems promo codes on its own page; it needs no SDK, so it opens even before the
    // store is configured. The result is read back with "check the result", as on iOS.
    if (offerCodeRouteFor(Capacitor.getPlatform()) === 'playRedeem') {
      try {
        window.open(PLAY_REDEEM_URL, '_blank');
        return { ok: true };
      } catch (raw) {
        return { ok: false, code: 'unknown', detail: raw instanceof Error ? raw.message : 'could not open Google Play' };
      }
    }
    if (!configured) return { ok: false, code: 'notConfigured', detail: 'the store is not configured' };
    try {
      // Apple's own redemption sheet (iOS 14+). The app never validates a code itself.
      await Purchases.presentCodeRedemptionSheet();
      return { ok: true };
    } catch (raw) {
      const failure = failureOf(raw);
      return { ok: false, code: failure.code, detail: failure.detail };
    }
  },

  openManageSubscriptions(url: string | null): boolean {
    // Both stores require subscription management to happen in their own page, never inside the app.
    // Capacitor hands a window.open of a foreign URL to the OS (WebViewDelegationHandler on iOS, an
    // intent on Android), which opens the store's subscriptions screen.
    const target = manageSubscriptionsUrlFor(Capacitor.getPlatform(), url);
    try {
      window.open(target, '_blank');
      return true;
    } catch {
      return false;
    }
  },

  onEntitlementChange(listener: (entitlement: StoreEntitlement) => void): () => void {
    if (!configured) return () => undefined;
    let callbackId: string | null = null;
    let stopped = false;
    void Purchases.addCustomerInfoUpdateListener((info: CustomerInfo) => {
      listener(entitlementFrom(info, boundUserId));
    })
      .then((id) => {
        if (stopped) void Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: id });
        else callbackId = id;
      })
      .catch(() => undefined);
    return () => {
      stopped = true;
      if (callbackId) void Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: callbackId }).catch(() => undefined);
      callbackId = null;
    };
  }
};

// Test seam only: the module keeps configure() state, and a test that drives the real adapter would
// otherwise leak it into the next one.
export function resetRevenueCatAdapterForTests(): void {
  configured = false;
  boundUserId = null;
}
